// @flow

import { all, put, takeEvery, call, select } from "redux-saga/effects";
import type { Action } from "redux-action";
import { push } from "connected-react-router";

import http from "@http";
import { route } from "@routes/helper";
import { isFirstCareUser } from "@modules/account/selectors";
import actionCreators from "@modules/service/actionCreator";
import type {
  StandardQuestionsResponse,
  StandardQuestion,
} from "@modules/service/reducer/standard-questions/types";
import { ApprovalStateEnum } from "@modules/service/constants";
import type {
  HierarchyAssignment,
  ApprovableContainer,
  HierarchyAssignmentSummary,
} from "@modules/service/types";
import {
  GetStandardQuestions,
  GetStandardQuestionActive,
  AddNewStandardQuestion,
  GetStandardQuestion,
  UpdateStandardQuestion,
  SubmitStandardQuestionForReview,
  CancelStandardQuestionReview,
  StartStandardQuestionDraft,
  DiscardStandardQuestionDraft,
  CancelStandardQuestionReviewAndDiscard,
  DuplicateStandardQuestion,
  DisableStandardQuestion,
  UpdateStandardQuestionAssignments,
} from "@modules/service/constants/actions";
import { sendSuccessMessage } from "@modules/app/actionCreator";

type ListResponse<T> = $Exact<{
  content: T,
  paginations: any,
}>;

type ItemResponse<T> = $Exact<T>;

type StandardQuestionsListRequest = $Exact<{
  clientId: string,
}>;

type AddStandardQuestionsRequest = $Exact<{
  clientId: string,
  draft: StandardQuestion,
  assignments: Array<HierarchyAssignment>,
}>;

export type StandardQuestionRequest = $Exact<{
  ...StandardQuestionsListRequest,
  stableId: string,
}>;

export type StandardQuestionSubmitForReviewRequest = $Exact<{
  ...StandardQuestionRequest,
  notes: string,
}>;
type StandardQuestionUpdateRequest = $Exact<{
  ...StandardQuestionRequest,
  draft: StandardQuestion,
}>;

export function* onGetStandardQuestions({
  payload,
}: Action<StandardQuestionsListRequest>): Generator<
  *,
  void,
  { data: ListResponse<Array<StandardQuestionsResponse>> },
> {
  try {
    if (!payload || !payload.clientId) {
      throw Error("Invalid payload provided");
    }

    const response = yield call(
      http.get,
      `/api/v2/service-admin/client/${payload.clientId}/service-config/absence-management/standard-question/question`,
    );

    yield put(actionCreators.GetStandardQuestionsSuccess(response.data));
  } catch (e) {
    yield put(actionCreators.GetStandardQuestionsError(e.response));
  }
}

export function* onGetStandardQuestionActive({
  payload,
}: Action<StandardQuestionRequest>): Generator<*, void, { data: StandardQuestion }> {
  try {
    if (!payload || !payload.clientId || !payload.stableId) {
      throw Error("Invalid payload provided");
    }

    const response = yield call(
      http.get,
      `/api/v1/service-admin/client/${payload.clientId}/service-config/absence-management/standard-question/question/${payload.stableId}/active`,
    );

    yield put(actionCreators.GetStandardQuestionActiveSuccess(response.data));
  } catch (e) {
    yield put(actionCreators.GetStandardQuestionActiveError(e.response));
  }
}

export function* onAddNewStandardQuestion({
  payload,
}: Action<AddStandardQuestionsRequest>): Generator<*, void, { data: StandardQuestion }> {
  try {
    if (!payload || !payload.clientId || !payload.draft || !payload.assignments) {
      throw Error("Invalid payload provided");
    }

    const response = yield call(
      http.post,
      `/api/v1/service-admin/client/${payload.clientId}/service-config/absence-management/standard-question/question`,
      payload.draft,
    );

    if (payload.assignments.length) {
      yield all([
        ...payload.assignments.map((a: HierarchyAssignment) => {
          return call(
            http.post,
            `/api/v1/service-admin/client/${payload.clientId}/service-config/absence-management/standard-question/question/${response.data.id}/assignment`,
            a,
          );
        }),
      ]);
    }

    const firstCareUser = yield select(isFirstCareUser);
    if (firstCareUser) {
      yield call(
        http.post,
        `/api/v1/service-admin/client/${payload.clientId}/service-config/absence-management/standard-question/question/${response.data.id}/submit-for-review`,
        {
          notes: "Automatically submitted",
        },
      );
    }

    yield all([
      put(actionCreators.GetStandardQuestionActiveSuccess(response.data)),
      put(actionCreators.GetStandardQuestions({ clientId: payload.clientId })),
      put(actionCreators.ShowAddStandardQuestionModal({ show: false, step: 1 })),
      put(sendSuccessMessage("STANDART_QUESTION_CREATED")),
    ]);
  } catch (e) {
    yield put(actionCreators.GetStandardQuestionActiveError(e.response));
  }
}

export function* onGetStandardQuestion({
  payload,
}: Action<StandardQuestionRequest>): Generator<*, void, { data: StandardQuestion }> {
  try {
    if (!payload || !payload.clientId || !payload.stableId) {
      throw Error("Invalid payload provided");
    }

    const stableResponse = yield call(
      http.get,
      `/api/v1/service-admin/client/${payload.clientId}/service-config/absence-management/standard-question/question/${payload.stableId}`,
    );
    const assignmentsResponse = yield call(
      http.get,
      `/api/v1/service-admin/client/${payload.clientId}/service-config/absence-management/standard-question/question/${payload.stableId}/assignment`,
    );

    yield put(actionCreators.GetStandardQuestionSuccess(stableResponse.data));
    yield put(actionCreators.SetStandardQuestionAssignments(assignmentsResponse.data));
  } catch (e) {
    yield put(actionCreators.GetStandardQuestionError(e.response));
  }
}

export function* onUpdateStandardQuestion({
  payload,
}: Action<StandardQuestionUpdateRequest>): Generator<
  *,
  void,
  { data: ItemResponse<ApprovableContainer<StandardQuestion>> },
> {
  try {
    if (!payload || !payload.clientId || !payload.stableId || !payload.draft) {
      throw Error("Invalid payload provided");
    }

    const response = yield call(
      http.put,
      `/api/v1/service-admin/client/${payload.clientId}/service-config/absence-management/standard-question/question/${payload.stableId}/draft`,
      payload.draft,
    );

    const firstCareUser = yield select(isFirstCareUser);
    if (firstCareUser) {
      yield call(
        http.post,
        `/api/v1/service-admin/client/${payload.clientId}/service-config/absence-management/standard-question/question/${payload.stableId}/submit-for-review`,
        {
          notes: "Automatically submitted",
        },
      );
    }

    yield all([
      put(actionCreators.UpdateStandardQuestionSuccess(response.data)),
      put(actionCreators.ShowStandardQuestionEditModal(false)),
      put(push(route("ADMINISTRATION_SERVICE_DYNAMIC_QUESTIONS", { clientId: payload.clientId }))),
      put(sendSuccessMessage("DRAFT_UPDATED")),
    ]);
  } catch (e) {
    yield put(actionCreators.UpdateStandardQuestionError(e.response));
  }
}

export function* onSubmitStandardQuestionForReview({
  payload,
}: Action<StandardQuestionSubmitForReviewRequest>): Generator<*, void, void> {
  try {
    if (!payload || !payload.clientId || !payload.stableId || !payload.notes) {
      throw Error("Invalid payload provided");
    }

    yield call(
      http.post,
      `/api/v1/service-admin/client/${payload.clientId}/service-config/absence-management/standard-question/question/${payload.stableId}/submit-for-review`,
      {
        notes: payload.notes,
      },
    );

    yield all([
      put(actionCreators.SubmitStandardQuestionForReviewSuccess()),
      put(actionCreators.SetShowSubmitStandardQuestionModal(false)),
      put(push(route("ADMINISTRATION_SERVICE_DYNAMIC_QUESTIONS", { clientId: payload.clientId }))),
      put(sendSuccessMessage("DRAFT_SUBMITTED")),
    ]);
  } catch (e) {
    yield put(actionCreators.SubmitStandardQuestionForReviewError(e.response));
  }
}

export function* onCancelStandardQuestionReview({
  payload,
}: Action<StandardQuestionRequest>): Generator<*, void, void> {
  try {
    if (!payload || !payload.clientId || !payload.stableId) {
      throw Error("Invalid payload provided");
    }

    yield call(
      http.post,
      `/api/v1/service-admin/client/${payload.clientId}/service-config/absence-management/standard-question/question/${payload.stableId}/cancel-review`,
    );

    yield all([
      put(actionCreators.CancelStandardQuestionReviewSuccess()),
      put(actionCreators.SetShowCancelStandardQuestionModal(false)),
      put(push(route("ADMINISTRATION_SERVICE_DYNAMIC_QUESTIONS", { clientId: payload.clientId }))),
      put(actionCreators.GetStandardQuestions({ clientId: payload.clientId })),
      put(sendSuccessMessage("DRAFT_CANCELED")),
    ]);
  } catch (e) {
    yield put(actionCreators.CancelStandardQuestionReviewError(e.response));
  }
}

export function* onStartStandardQuestionDraft({
  payload,
}: Action<StandardQuestionRequest>): Generator<*, void, { data: StandardQuestion }> {
  try {
    if (!payload || !payload.clientId || !payload.stableId) {
      throw Error("Invalid payload provided");
    }

    const response = yield call(
      http.get,
      `/api/v1/service-admin/client/${payload.clientId}/service-config/absence-management/standard-question/question/${payload.stableId}`,
    );

    yield all([
      put(actionCreators.StartStandardQuestionDraftSuccess(response.data)),
      put(actionCreators.ShowStandardQuestionEditModal(true)),
    ]);
  } catch (e) {
    yield put(actionCreators.StartStandardQuestionDraftError(e.response));
  }
}

export function* onDiscardStandardQuestionDraft({
  payload,
}: Action<StandardQuestionRequest>): Generator<*, void, void> {
  try {
    if (!payload || !payload.clientId || !payload.stableId) {
      throw Error("Invalid payload provided");
    }

    yield call(
      http.delete,
      `/api/v1/service-admin/client/${payload.clientId}/service-config/absence-management/standard-question/question/${payload.stableId}/draft`,
    );

    yield all([
      put(actionCreators.DiscardStandardQuestionDraftSuccess()),
      put(actionCreators.SetShowDiscardStandardQuestionModal(false)),
      put(push(route("ADMINISTRATION_SERVICE_DYNAMIC_QUESTIONS", { clientId: payload.clientId }))),
      put(sendSuccessMessage("DRAFT_DISCARDED")),
    ]);
    yield call(onRefresh, { clientId: payload.clientId });
  } catch (e) {
    yield put(actionCreators.DiscardStandardQuestionDraftError(e.response));
  }
}

export function* onCancelStandardQuestionReviewAndDiscard({
  payload,
}: Action<StandardQuestionRequest>): Generator<*, void, void> {
  try {
    if (!payload || !payload.clientId || !payload.stableId) {
      throw Error("Invalid payload provided");
    }

    yield call(
      http.post,
      `/api/v1/service-admin/client/${payload.clientId}/service-config/absence-management/standard-question/question/${payload.stableId}/cancel-review`,
    );
    yield call(
      http.delete,
      `/api/v1/service-admin/client/${payload.clientId}/service-config/absence-management/standard-question/question/${payload.stableId}/draft`,
    );

    yield all([
      put(actionCreators.CancelStandardQuestionReviewSuccess()),
      put(actionCreators.SetShowCancelDiscardStandardQuestionModal(false)),
      put(push(route("ADMINISTRATION_SERVICE_DYNAMIC_QUESTIONS", { clientId: payload.clientId }))),
      put(actionCreators.GetStandardQuestions({ clientId: payload.clientId })),
      put(sendSuccessMessage("DRAFT_CANCELED_AND_DISCARDED")),
    ]);
  } catch (e) {
    yield put(actionCreators.CancelStandardQuestionReviewAndDiscardError(e.response));
  }
}

export function* onDuplicateStandardQuestion({
  payload,
}: Action<StandardQuestionRequest>): Generator<
  *,
  void,
  { data: ItemResponse<ApprovableContainer<StandardQuestion>> },
> {
  try {
    if (!payload || !payload.clientId || !payload.stableId) {
      throw Error("Invalid payload provided");
    }

    const response = yield call(
      http.get,
      `/api/v1/service-admin/client/${payload.clientId}/service-config/absence-management/standard-question/question/${payload.stableId}/active`,
    );

    yield all([
      put(actionCreators.DuplicateStandardQuestionSuccess(response.data)),
      put(actionCreators.SetStandardQuestionAssignments([])),
      put(actionCreators.ShowAddStandardQuestionModal({ show: true, step: 1 })),
      put(push(route("ADMINISTRATION_SERVICE_DYNAMIC_QUESTIONS", { clientId: payload.clientId }))),
    ]);
  } catch (e) {
    yield put(actionCreators.DuplicateStandardQuestionError(e.response));
  }
}

export type StandardQuestionDisableRequest = $Exact<{
  ...StandardQuestionRequest,
  approvable: ApprovableContainer<StandardQuestion>,
}>;

export function* onDisableStandardQuestion({
  payload,
}: Action<StandardQuestionDisableRequest>): Generator<*, void, *> {
  try {
    if (!payload || !payload.clientId || !payload.stableId) {
      throw Error("Invalid payload provided");
    }
    const draft =
      payload.approvable.approvalState === ApprovalStateEnum.ACTIVE_ONLY
        ? payload.approvable.active
        : payload.approvable.draft;

    if (!draft) {
      throw Error("Invalid draft");
    }

    const response = yield call(
      http.put,
      `/api/v1/service-admin/client/${payload.clientId}/service-config/absence-management/standard-question/question/${payload.stableId}/draft`,
      {
        ...draft,
        isActive: !draft.isActive,
      },
    );

    yield call(
      http.post,
      `/api/v1/service-admin/client/${payload.clientId}/service-config/absence-management/standard-question/question/${payload.stableId}/submit-for-review`,
      {
        notes: "Standard Question disabled and resubmitted for review",
      },
    );

    yield all([
      put(actionCreators.DisableStandardQuestionSuccess(response.data)),
      put(actionCreators.SetShowDisableStandardQuestionModal(false)),
      put(push(route("ADMINISTRATION_SERVICE_DYNAMIC_QUESTIONS", { clientId: payload.clientId }))),
      put(sendSuccessMessage("DRAFT_UPDATED")),
    ]);

    yield call(onRefresh, { clientId: payload.clientId });
  } catch (e) {
    yield put(actionCreators.DisableStandardQuestionError(e.response));
  }
}

// REFRESH

type RefreshRequest = $Exact<{
  clientId: string,
  stableId?: string,
}>;

export function* onRefresh(params: RefreshRequest): Generator<*, void, Object> {
  try {
    yield put(actionCreators.GetStandardQuestions({ clientId: params.clientId }));
    if (params.stableId) {
      yield put(
        actionCreators.GetStandardQuestion({
          clientId: params.clientId,
          stableId: params.stableId,
        }),
      );
    }
  } catch (e) {
    throw e;
  }
}

type StandardQuestionUpdateAssignmentsRequest = $Exact<{
  container: ApprovableContainer<StandardQuestion>,
  assignments: Array<HierarchyAssignment>,
}>;

export function* onUpdateStandardQuestionAssignments({
  payload,
}: Action<StandardQuestionUpdateAssignmentsRequest>): Generator<*, void, *> {
  try {
    if (!payload || !payload.container || !payload.assignments) {
      throw Error("Invalid payload provided");
    }

    const { data: prevAssignments } = yield call(
      http.get,
      `/api/v1/service-admin/client/${payload.container.clientId}/service-config/absence-management/standard-question/question/${payload.container.id}/assignment`,
    );
    if (prevAssignments && prevAssignments.length) {
      yield all([
        ...prevAssignments.map((a: HierarchyAssignmentSummary) => {
          return call(
            http.delete,
            `/api/v1/service-admin/client/${payload.container.clientId}/service-config/absence-management/standard-question/question/${payload.container.id}/assignment/${a.id}`,
          );
        }),
      ]);
    }

    if (payload.assignments.length) {
      yield all([
        ...payload.assignments.map((a: HierarchyAssignment) => {
          return call(
            http.post,
            `/api/v1/service-admin/client/${payload.container.clientId}/service-config/absence-management/standard-question/question/${payload.container.id}/assignment`,
            a,
          );
        }),
      ]);
    }

    yield call(onRefresh, {
      clientId: payload.container.clientId,
      stableId: payload.container.id,
    });
    yield all([
      put(actionCreators.ShowEditStandardQuestionAssignmentsModal(false)),
      put(
        push(
          route("ADMINISTRATION_SERVICE_DYNAMIC_QUESTIONS", {
            clientId: payload.container.clientId,
          }),
        ),
      ),
      put(sendSuccessMessage("DRAFT_UPDATED")),
    ]);
  } catch (e) {
    yield put(actionCreators.UpdateStandardQuestionAssignmentsError(e.message));
  }
}

export function* StandardQuestionsSagas(): Generator<*, void, void> {
  yield all([
    takeEvery(GetStandardQuestions, onGetStandardQuestions),
    takeEvery(GetStandardQuestionActive, onGetStandardQuestionActive),
    takeEvery(AddNewStandardQuestion, onAddNewStandardQuestion),
    takeEvery(GetStandardQuestion, onGetStandardQuestion),
    takeEvery(UpdateStandardQuestion, onUpdateStandardQuestion),
    takeEvery(SubmitStandardQuestionForReview, onSubmitStandardQuestionForReview),
    takeEvery(CancelStandardQuestionReview, onCancelStandardQuestionReview),
    takeEvery(StartStandardQuestionDraft, onStartStandardQuestionDraft),
    takeEvery(DiscardStandardQuestionDraft, onDiscardStandardQuestionDraft),
    takeEvery(CancelStandardQuestionReviewAndDiscard, onCancelStandardQuestionReviewAndDiscard),
    takeEvery(DuplicateStandardQuestion, onDuplicateStandardQuestion),
    takeEvery(DisableStandardQuestion, onDisableStandardQuestion),
    takeEvery(UpdateStandardQuestionAssignments, onUpdateStandardQuestionAssignments),
  ]);
}

export default StandardQuestionsSagas;
